import { jssToCSS } from "@universityofmaryland/web-utilities-library/styles";
import { isElementStyles, isStyleDefinition } from "./types.js";
class StyleManager {
  constructor() {
    this.styleQueue = [];
    this.compiled = /* @__PURE__ */ new Map();
    this.styleIds = /* @__PURE__ */ new Set();
  }
  /**
   * Add styles to the registry
   * Automatically handles ElementStyles structure and StyleDefinition objects
   *
   * @param styles - Style definition from styles library or ElementStyles object
   * @param className - Class name to use as selector (null will skip style application)
   * @param priority - Priority for style ordering (higher = applied last)
   */
  add(styles, className, priority = 0) {
    if (!className) {
      return;
    }
    if (isElementStyles(styles)) {
      this.addElementStyles(styles, className, priority);
    } else if (isStyleDefinition(styles)) {
      this.addStyleDefinition(styles, className, priority);
    } else {
      this.addPlainStyles(styles, className, priority);
    }
  }
  /**
   * Add ElementStyles structure (element, pseudoBefore, siblingAfter, subElement)
   */
  addElementStyles(styles, className, priority) {
    if (styles.element) {
      this.addStyleEntry(`.${className}`, styles.element, priority, "element");
    }
    if (styles.pseudoBefore) {
      this.addStyleEntry(
        `.${className}::before`,
        styles.pseudoBefore,
        priority,
        "pseudo-before"
      );
    }
    if (styles.siblingAfter) {
      this.addStyleEntry(
        `.${className} + *`,
        styles.siblingAfter,
        priority,
        "sibling-after"
      );
    }
    if (styles.subElement) {
      this.addStyleEntry(
        `.${className} > *`,
        styles.subElement,
        priority,
        "sub-element"
      );
    }
  }
  /**
   * Add StyleDefinition from styles library
   * These have a className property and style properties
   */
  addStyleDefinition(definition, className, priority) {
    const { className: defClassName, ...styleProps } = definition;
    const targetClassName = Array.isArray(defClassName) ? defClassName[0] : defClassName || className;
    if (isElementStyles(styleProps)) {
      this.addElementStyles(styleProps, targetClassName, priority);
    } else {
      this.addStyleEntry(
        `.${targetClassName}`,
        styleProps,
        priority,
        "definition"
      );
    }
  }
  /**
   * Add plain JSS object
   */
  addPlainStyles(styles, className, priority) {
    const hasSelectors = Object.keys(styles).some(
      (key) => key.startsWith(".") || key.startsWith("#") || key.startsWith("&")
    );
    if (hasSelectors) {
      this.addStyleEntry(
        `.${className}`,
        styles,
        priority,
        "jss-with-selectors"
      );
    } else {
      this.addStyleEntry(`.${className}`, styles, priority, "plain-object");
    }
  }
  /**
   * Add a single style entry with deduplication
   */
  addStyleEntry(selector, styles, priority, type) {
    const id = this.createStyleId(selector, styles, type);
    if (this.styleIds.has(id)) {
      return;
    }
    this.styleIds.add(id);
    this.styleQueue.push({ selector, styles, priority, id, type });
  }
  /**
   * Create a unique ID for style deduplication
   */
  createStyleId(selector, styles, type) {
    const content = `${selector}:${type}:${JSON.stringify(styles)}`;
    return this.simpleHash(content);
  }
  /**
   * Simple string hash function
   */
  simpleHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return hash.toString(36);
  }
  /**
   * Compile all queued styles into a CSS string using CSS cascade order
   *
   * Instead of merging styles at the object level, this generates separate CSS
   * for each priority level and concatenates them. This preserves duplicate
   * properties and lets CSS cascade determine which styles win.
   *
   * Priority order (lower priority first, higher priority last):
   * - Priority 1: .withStyles() - custom inline styles
   * - Priority 2: .styled() - design system presets
   *
   * Example output:
   * ```css
   * .test { color: black; }      // Priority 1
   * .test { color: green; }      // Priority 2 (wins via cascade)
   * ```
   *
   * @returns Compiled CSS string with styles in cascade order
   */
  compile() {
    if (this.styleQueue.length === 0) {
      return "";
    }
    const sorted = [...this.styleQueue].sort((a, b) => a.priority - b.priority);
    const byPriority = /* @__PURE__ */ new Map();
    sorted.forEach((entry) => {
      if (!byPriority.has(entry.priority)) {
        byPriority.set(entry.priority, []);
      }
      byPriority.get(entry.priority).push(entry);
    });
    const cssByPriority = [];
    Array.from(byPriority.keys()).sort((a, b) => a - b).forEach((priority) => {
      const entries = byPriority.get(priority);
      const grouped = /* @__PURE__ */ new Map();
      entries.forEach(({ selector, styles }) => {
        if (!grouped.has(selector)) {
          grouped.set(selector, {});
        }
        const currentStyles = grouped.get(selector);
        const mergedStyles = this.deepMerge(styles);
        grouped.set(selector, this.intelligentMerge(currentStyles, mergedStyles));
      });
      const cssRules = [];
      grouped.forEach((styles, selector) => {
        try {
          const css = jssToCSS({
            styleObj: { [selector]: styles }
          });
          if (css && css.trim()) {
            cssRules.push(css);
          }
        } catch (error) {
          console.error(
            `Error compiling styles for selector "${selector}":`,
            error
          );
        }
      });
      if (cssRules.length > 0) {
        cssByPriority.push(cssRules.join("\n"));
      }
    });
    return cssByPriority.join("\n");
  }
  /**
   * Deep merge style objects
   * Handles nested selectors, media queries, and container queries
   *
   * Special handling for:
   * - @media queries: Merges properties within matching queries
   * - @container queries: Merges properties within matching queries
   * - @supports queries: Merges properties within matching queries
   * - Nested selectors (&:hover, etc): Merges properties within matching selectors
   */
  deepMerge(source) {
    const result = {};
    Object.entries(source).forEach(([key, value]) => {
      if (value && typeof value === "object" && !Array.isArray(value)) {
        result[key] = this.deepMerge(value);
      } else {
        result[key] = value;
      }
    });
    return result;
  }
  /**
   * Intelligently merge two style objects
   * Recursively merges nested queries (@media, @container, etc.) and selectors
   *
   * @param target - The target object to merge into
   * @param source - The source object to merge from
   * @returns Merged object with intelligent query composition
   */
  intelligentMerge(target, source) {
    const result = { ...target };
    Object.entries(source).forEach(([key, value]) => {
      const isNestedRule = key.startsWith("@media") || key.startsWith("@container") || key.startsWith("@supports") || key.startsWith("&") || key.startsWith(":");
      if (isNestedRule && typeof value === "object" && !Array.isArray(value)) {
        if (result[key] && typeof result[key] === "object" && !Array.isArray(result[key])) {
          result[key] = this.intelligentMerge(result[key], value);
        } else {
          result[key] = value;
        }
      } else if (typeof value === "object" && !Array.isArray(value)) {
        if (result[key] && typeof result[key] === "object" && !Array.isArray(result[key])) {
          result[key] = this.intelligentMerge(result[key], value);
        } else {
          result[key] = value;
        }
      } else {
        result[key] = value;
      }
    });
    return result;
  }
  /**
   * Clone the registry for immutable operations
   */
  clone() {
    const cloned = new StyleManager();
    cloned.styleQueue = [...this.styleQueue];
    cloned.compiled = new Map(this.compiled);
    cloned.styleIds = new Set(this.styleIds);
    return cloned;
  }
  /**
   * Clear all styles
   */
  clear() {
    this.styleQueue = [];
    this.compiled.clear();
    this.styleIds.clear();
  }
  /**
   * Get current style count (for debugging)
   */
  getStyleCount() {
    return this.styleQueue.length;
  }
  /**
   * Get unique style count (after deduplication)
   */
  getUniqueStyleCount() {
    return this.styleIds.size;
  }
}
function createStyleTag(css, id) {
  const style = document.createElement("style");
  if (id) {
    style.id = id;
  }
  style.textContent = css;
  return style;
}
function injectStyles(css, id) {
  if (id) {
    const existing = document.getElementById(id);
    if (existing) {
      existing.textContent = css;
      return;
    }
  }
  const style = createStyleTag(css, id);
  document.head.appendChild(style);
}
export {
  StyleManager,
  createStyleTag,
  injectStyles
};
//# sourceMappingURL=StyleManager.js.map
